// src/App.jsx
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

// Components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './utils/ScrollToTop';

// Currency
import { CurrencyProvider } from './context/CurrencyContext';

// Pages
import Home from './pages/Home';
import About from './pages/about';
import ProductDetails from './pages/ProductDetails';
import Contact from './pages/Contact';
import CollectionsPage from './pages/CollectionsPage';
import CollectionDetail from './pages/CollectionDetail';
import HotDeals from './pages/HotDeals';
import ShopByReelPage from './pages/ShopByReelPage';
import ReelProductsPage from './pages/ReelProductsPage';
import ReelProductDetail from './pages/ReelProductDetail';
import AdminDashboard from './pages/AdminDashboard';
import AdminLogin from './pages/AdminLogin';
import AccountPage from './pages/AccountPage';
import CartPage from './pages/CartPage';
import Checkout from './pages/Checkout';
import OrderSuccess from './pages/OrderSuccess';
import { isAdminAuthenticated } from './api/auth';
import OrderDetailPage from './pages/OrderDetailPage';
import ProductsPage from './pages/ProductsPage';

const pageWrap = (children) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    transition={{ duration: 0.3 }}
  >
    {children}
  </motion.div>
);

const AdminRoute = ({ children }) => {
  return isAdminAuthenticated() ? children : <Navigate to="/admin/login" replace />;
};

const AppInner = () => {
  const location    = useLocation();
  const isAdminPage = location.pathname.startsWith('/admin');

  return (
    <>
      <ScrollToTop />
      <div className="min-h-screen bg-white dark:bg-dark-900 text-gray-900 dark:text-white transition-colors duration-200">

        {/* Currency only applies to customer-facing pages */}
        {!isAdminPage ? (
          <CurrencyProvider>
            <Navbar />
            <AnimatePresence mode="wait">
              <Routes location={location} key={location.pathname}>
                <Route path="/"                        element={pageWrap(<Home />)} />
                <Route path="/about"                   element={pageWrap(<About />)} />
                <Route path="/products/:productId"     element={pageWrap(<ProductDetails />)} />
                <Route path="/contact"                 element={pageWrap(<Contact />)} />
                <Route path="/collections"             element={pageWrap(<CollectionsPage />)} />
                <Route path="/collections/:slug"       element={pageWrap(<CollectionDetail />)} />
                <Route path="/deals"                   element={pageWrap(<HotDeals />)} />
                <Route path="/account"                 element={pageWrap(<AccountPage />)} />
                <Route path="/cart"                    element={pageWrap(<CartPage />)} />
                <Route path="/reels"                   element={pageWrap(<ShopByReelPage />)} />
                <Route path="/products"                element={pageWrap(<ProductsPage />)} />
                <Route path="/reels/:reelId"           element={pageWrap(<ReelProductsPage />)} />
                <Route path="/reels/:reelId/:productId" element={pageWrap(<ReelProductDetail />)} />
                <Route path="/checkout"                element={<Checkout />} />
                <Route path="/order-success"           element={<OrderSuccess />} />
                <Route path="/orders/:id"              element={<OrderDetailPage />} />
                <Route path="/login"                   element={<Navigate to="/account" replace />} />
              </Routes>
            </AnimatePresence>
            <Footer />
          </CurrencyProvider>
        ) : (
          <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              } />
            </Routes>
          </AnimatePresence>
        )}

      </div>
    </>
  );
};

function App() {
  return (
    <Router>
      <AppInner />
    </Router>
  );
}

export default App;